"use client";

import React, { useEffect, useMemo, useState } from "react";
import Alert from "antd/es/alert";
import Flex from "antd/es/flex";
import Spin from "antd/es/spin";
import Text from "antd/es/typography/Text";
import Title from "antd/es/typography/Title";
import Paragraph from "antd/es/typography/Paragraph";

import {
  BookingDateSelector,
  BookingTimeButton,
  bookingPageTitleStyle,
  slotsContainerStyle,
  useBookingDate,
} from "@openings/template-components";

import type { DbDateString, DbTimeString } from "@openings/api-client";
import type { SelectedServiceDTO } from "@/app/components/Booking/MemberBooking/types";
import { bookingMetaTextStyle } from "@/app/components/BookingPage/bookingTextStyles";
import { formatDuration, formatPrice } from "@openings/booking-core";
import { formatCompactTime, getMinBookingNoticeNote } from "@utils/core";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import { useApp } from "@/lib/hooks/useAppointment";
import { useLocale } from "@/lib/i18n";
import {
  ClockCircleOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  createQuoteOutcomeCallbacks,
  resolveQuoteBookingState,
  type QuoteBookingLocalOutcome,
  type QuoteBookingState,
} from "./quoteBookingState";
import { quoteFormPolicy } from "./quoteBookingProps";
import styles from "./quoteBooking.module.css";

function formatDate(date: Date): DbDateString {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}` as DbDateString;
}

export type QuoteBookingClientProps = {
  salonId: string;
  requestId: string;
  /** Identifies the exact quote revision accepted by this booking. */
  quotePublicId: string;
  teamMemberId: string;
  teamMemberName?: string;
  scheduleId: string;
  /**
   * Schedule id used for openings lookup — resolves to the team member's
   * child schedule so the booking UI respects their actual working hours,
   * not the business's. Falls back to `scheduleId` when absent.
   */
  bookingScheduleId?: string;
  scheduleTitle?: string;
  scheduleAddress?: string;
  serviceId?: string;
  serviceTitle: string;
  price: number;
  durationMinutes: number;
  /** False when the business turned its window off; undefined when unknown. */
  cancellationPolicyEnabled?: boolean;
  cancellationPolicyHours?: number | null;
  cancellationPolicyNote?: string | null;
  lateGraceMinutes?: number | null;
  /**
   * The business's customer booking window (`customerBookingHorizonDays`).
   * The customer still picks the time, so it applies to quotes too.
   */
  bookingHorizonDays?: number | null;
  bookingTimeZone?: string | null;
  /**
   * The quoted service's minimum notice in minutes. The API leaves the
   * starts inside it out of the openings; the quote card says so.
   */
  minBookingNoticeMinutes?: number | null;
  /**
   * Upfront-payment policy of the quoted service, applied to `price`. It is
   * what routes the appointment flow through the card step; `depositAmount`
   * arrives already clamped to the quoted price by the API. Defaults to no
   * upfront payment.
   */
  paymentRequirement?: "NONE" | "DEPOSIT" | "FULL";
  depositAmount?: number | null;
  /** Server-derived acceptance/payment state for this exact quote revision. */
  initialBookingState?: QuoteBookingState;
  /** Deterministic entry date for dev/test harnesses; live links use today. */
  initialDate?: DbDateString;
};

export default function QuoteBookingClient(props: QuoteBookingClientProps) {
  const {
    salonId,
    requestId,
    quotePublicId,
    teamMemberId,
    teamMemberName,
    scheduleId,
    bookingScheduleId,
    scheduleTitle,
    scheduleAddress,
    serviceId,
    serviceTitle,
    price,
    durationMinutes,
    cancellationPolicyHours,
    cancellationPolicyNote,
    lateGraceMinutes,
    bookingHorizonDays,
    bookingTimeZone,
    minBookingNoticeMinutes = null,
    paymentRequirement = "NONE",
    depositAmount = null,
    initialBookingState = "AVAILABLE",
    initialDate,
  } = props;

  const { openAppointmentForm } = useApp();
  const { t } = useLocale();
  // On the quote location's calendar, the one the openings API reads `date`
  // in (BF-DEC-015).
  const [date, setDate] = useBookingDate(bookingTimeZone, initialDate);
  const [openings, setOpenings] = useState<DbTimeString[]>([]);
  /**
   * The zone the openings were computed in. Older API builds omit it, so the
   * server-resolved quote zone stays the fallback (BF-DRIFT-016).
   */
  const [openingsTimeZone, setOpeningsTimeZone] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localOutcome, setLocalOutcome] =
    useState<QuoteBookingLocalOutcome | null>(null);
  const bookingState = resolveQuoteBookingState(
    initialBookingState,
    quotePublicId,
    localOutcome,
  );
  const quoteUnavailable = bookingState !== "AVAILABLE";
  const quoteOutcomeCallbacks = useMemo(
    () => createQuoteOutcomeCallbacks(quotePublicId, setLocalOutcome),
    [quotePublicId],
  );

  const selectedService: SelectedServiceDTO | null = useMemo(() => {
    if (!serviceId) return null;
    return {
      id: serviceId,
      salonId,
      title: serviceTitle,
      description: "",
      duration: durationMinutes,
      price,
      up: false,
      hasPayAdvance: false,
      hasConsultation: false,
      // Inherited from the quoted service; `isCreditCardRequired` in the
      // appointment flow reads these to route through the card step.
      paymentRequirement,
      depositAmount,
      options: [],
    };
  }, [
    depositAmount,
    durationMinutes,
    paymentRequirement,
    price,
    salonId,
    serviceId,
    serviceTitle,
  ]);

  const upfrontPaymentLabel =
    paymentRequirement === "FULL"
      ? `${formatPrice(price)} due when you book`
      : paymentRequirement === "DEPOSIT"
        ? `${formatPrice(
            depositAmount != null && depositAmount > 0 ? depositAmount : price,
          )} deposit due when you book`
        : null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await bookingFlowApi.getScheduleOpenings({
          scheduleId: bookingScheduleId ?? scheduleId,
          date: formatDate(date),
          serviceDuration: durationMinutes,
          // The API leaves out the starts inside the service's notice.
          ...(serviceId ? { serviceIds: [serviceId] } : {}),
        });
        if (!cancelled) {
          setOpenings(res.data.openings as DbTimeString[]);
          setOpeningsTimeZone(res.data.timezone ?? null);
        }
      } catch (e: any) {
        if (!cancelled) {
          setOpenings([]);
          setOpeningsTimeZone(null);
          setError(e?.message ?? "Could not load availability");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // A consumed quote must not fetch availability behind its booked state.
    if (selectedService && !quoteUnavailable) {
      void load();
    }

    return () => {
      cancelled = true;
    };
  }, [
    quoteUnavailable,
    date,
    durationMinutes,
    bookingScheduleId,
    scheduleId,
    serviceId,
    selectedService,
  ]);

  const minNoticeNote = getMinBookingNoticeNote(minBookingNoticeMinutes);

  if (!selectedService) {
    return (
      <Alert
        type="warning"
        showIcon
        message="This quote cannot be booked online"
        description="Please contact the business to schedule this quote."
      />
    );
  }

  // The appointment dialog owns the result scene (`AppointmentConfirmation`,
  // same as every other standalone booking). This is only what the page shows
  // behind that dialog and after "Done", so the consumed quote cannot be
  // re-booked from a stale picker. Same title/meta treatment as
  // `InvalidLinkMessage` — the quote route shares the booking page's B&W system.
  if (bookingState === "CONFIRMED") {
    return (
      <div data-quote-booked>
        <h3 style={{ ...bookingPageTitleStyle, margin: "0 0 4px" }}>
          {t.youreBooked}
        </h3>
        <p style={{ ...bookingMetaTextStyle, margin: 0 }}>
          {t.appointmentConfirmed}
        </p>
      </div>
    );
  }

  if (bookingState === "PAYMENT_REVIEW") {
    return (
      <div data-quote-payment-review>
        <h3 style={{ ...bookingPageTitleStyle, margin: "0 0 4px" }}>
          Booking is being confirmed
        </h3>
        <p style={{ ...bookingMetaTextStyle, margin: 0 }}>
          Payment could not be confirmed yet. Please refresh shortly or contact
          the business before trying again.
        </p>
      </div>
    );
  }

  if (bookingState === "CONSUMED") {
    return (
      <div data-quote-consumed>
        <h3 style={{ ...bookingPageTitleStyle, margin: "0 0 4px" }}>
          This quote has already been used
        </h3>
        <p style={{ ...bookingMetaTextStyle, margin: 0 }}>
          Contact the business if you need to make another appointment.
        </p>
      </div>
    );
  }

  return (
    <Flex vertical gap={16}>
      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
          Your Quote
        </Title>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          Review your quote details and pick a time to book.
        </Paragraph>
      </div>

      <div className={styles.grid}>
        <div className={styles.infoCol}>
          <div className={styles.quoteCard}>
            <div className={styles.serviceHeader}>
              <Title level={5} className={styles.serviceTitle}>
                {serviceTitle}
              </Title>
              <Text className={styles.priceBadge}>{formatPrice(price)}</Text>
            </div>

            <div className={styles.detailRow}>
              <ClockCircleOutlined />
              <Text type="secondary">{formatDuration(durationMinutes)}</Text>
            </div>

            {upfrontPaymentLabel ? (
              <div className={styles.detailRow} data-quote-upfront-payment>
                <CreditCardOutlined />
                <Text type="secondary">{upfrontPaymentLabel}</Text>
              </div>
            ) : null}

            {minNoticeNote ? (
              <div className={styles.detailRow} data-quote-min-notice>
                <InfoCircleOutlined />
                <Text type="secondary">{minNoticeNote}</Text>
              </div>
            ) : null}

            {scheduleTitle ? (
              <div className={styles.scheduleBlock}>
                <EnvironmentOutlined />
                <div>
                  <Text>{scheduleTitle}</Text>
                  {scheduleAddress ? (
                    <div>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {scheduleAddress}
                      </Text>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {typeof cancellationPolicyHours === "number" ? (
              <div className={styles.detailRow} style={{ marginTop: 12 }}>
                <InfoCircleOutlined />
                <Text type="secondary">
                  Cancellation policy: {cancellationPolicyHours} hours
                  {cancellationPolicyNote ? ` · ${cancellationPolicyNote}` : ""}
                </Text>
              </div>
            ) : null}

            {teamMemberName ? (
              <div className={styles.scheduleBlock}>
                <Text type="secondary">
                  Quoted by <Text strong>{teamMemberName}</Text>
                </Text>
              </div>
            ) : null}
          </div>
        </div>

        <div className={styles.bookingCol}>
          <div>
            <Title level={5} style={{ marginBottom: 8, marginTop: 0 }}>
              Choose a Date
            </Title>

            <BookingDateSelector
              selectedDate={date}
              setDate={setDate}
              filteredWorkDays={[]}
              excludedDates={[]}
              bookingHorizonDays={bookingHorizonDays}
              timeZone={bookingTimeZone}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginBottom: 8, marginTop: 0 }}>
              Choose a time
            </Title>

            {loading ? (
              <Spin />
            ) : error ? (
              <Alert type="error" showIcon message={error} />
            ) : openings.length === 0 ? (
              <Text type="secondary">No openings for this day.</Text>
            ) : (
              <div style={slotsContainerStyle}>
                {openings.map((time) => (
                  <BookingTimeButton
                    key={time}
                    time={time as DbTimeString}
                    isSelected={false}
                    label={formatCompactTime(time, {
                      uppercase: true,
                      alwaysShowMinutes: true,
                    })}
                    onClick={() => {
                      openAppointmentForm({
                        salonId,
                        userId: teamMemberId,
                        teamMemberId,
                        scheduleId: bookingScheduleId ?? scheduleId,
                        scheduleName: scheduleTitle,
                        scheduleAddress,
                        stylistName: teamMemberName ?? "Team member",
                        services: [selectedService],
                        date: formatDate(date),
                        time,
                        timezone:
                          openingsTimeZone ?? bookingTimeZone ?? undefined,
                        requestId,
                        quotePublicId,
                        ...quoteFormPolicy(props),
                        completionMode: "inline",
                        // NOT `onAfter`: the lifecycle callbacks update the
                        // exact quote revision while the shared flow keeps its
                        // inline confirmation or closes a pending payment.
                        ...quoteOutcomeCallbacks,
                      });
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Flex>
  );
}
